<template>
  <div class="ps-filter-bar">
    <div class="ps-filter-bar__search">
      <input
        class="ps-input"
        type="text"
        :value="keyword"
        :placeholder="placeholder"
        @input="onKeyword"
      />
      <img
        class="ps-filter-bar__search-icon"
        :src="iconSearch"
        alt="搜索"
        title="搜索"
        @click="onSearch"
      />
    </div>
    <select
      v-if="!isGuideRole"
      class="ps-select"
      :class="{ 'ps-select--wide': isTwoLevel }"
      :value="auditState"
      @change="onStatus"
    >
      <option value="">全部状态</option>
      <template v-if="isTwoLevel">
        <option
          v-for="opt in twoLevelFilterOptions"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </template>
      <template v-else>
        <option v-if="showDraftOption" value="0">{{ draftStateLabel }}</option>
        <option value="1">待审核</option>
        <option value="2">审核通过</option>
        <option value="3">审核驳回</option>
      </template>
    </select>
    <div class="ps-filter-bar__year">
      <a-date-picker
        class="ps-year-picker"
        style="width: 103px"
        :value="year"
        :open="yearOpen"
        :locale="locale"
        format="YYYY"
        mode="year"
        placeholder="全部年份"
        allowClear
        @openChange="onYearOpenChange"
        @panelChange="onYearPanelChange"
        @change="onYearClear"
      />
    </div>
    <span class="ps-filter-bar__spacer"></span>
    <button
      v-if="showAdd"
      type="button"
      class="ps-btn ps-btn--blue"
      @click="$emit('add')"
    >
      <img :src="iconAdd" alt="" />
      <span>新增项目</span>
    </button>
    <button
      v-if="showBatchSubmit"
      type="button"
      class="ps-btn ps-btn--outline-blue"
      @click="$emit('batch-submit')"
    >
      批量提交
    </button>
    <slot name="extra"></slot>
  </div>
</template>

<script>
import locale from "ant-design-vue/es/date-picker/locale/zh_CN";
import "moment/locale/zh-cn";
import { getModuleConfig, isTwoLevelAuditModule } from "./moduleConfig.js";
import iconSearch from "@/assets/images/projectShell/icon-search.svg";
import iconAdd from "@/assets/images/projectShell/icon-add.png";

export default {
  name: "ProjectFilterBar",
  props: {
    moduleType: {
      type: String,
      default: "studio",
    },
    keyword: {
      type: String,
      default: "",
    },
    auditState: {
      type: String,
      default: "",
    },
    /** moment 或空，与原 tableView 一致 */
    year: {
      type: [Object, String],
      default: null,
    },
    showAdd: {
      type: Boolean,
      default: true,
    },
    showBatchSubmit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      locale,
      yearOpen: false,
      iconSearch,
      iconAdd,
    };
  },
  computed: {
    placeholder() {
      return getModuleConfig(this.moduleType).searchPlaceholder;
    },
    isTwoLevel() {
      return isTwoLevelAuditModule(this.moduleType);
    },
    /** 教育厅角色只看审核通过数据，无需状态筛选下拉框 */
    isGuideRole() {
      return this.$store.state.role === "education-guide-role";
    },
    draftStateLabel() {
      return this.isTwoLevel ? "未提交" : "已保存";
    },
    /** 两级审核模块：学校/审核员不展示「未提交」 */
    showDraftOption() {
      if (
        this.isTwoLevel &&
        (this.$store.state.role === "school-role" ||
          this.$store.state.role === "audit-role")
      ) {
        return false;
      }
      return true;
    },
    /** 两级审核模块：状态筛选选项与角色对应
     *  教育厅(education-guide-role) 仅见：审核通过
     *  教育局/厅/审核员(audit-role)/评分员(score-role) 仅见：待教育局/厅审核、审核通过、教育局驳回
     *  学校/教师/其他角色 可见全部状态 */
    twoLevelFilterOptions() {
      const role = this.$store.state.role;
      // 教育厅角色只查看教育局/厅审核通过的数据
      if (role === "education-guide-role") {
        return [{ value: "2", label: "审核通过" }];
      }
      if (
        role === "audit-role" ||
        role === "education-role" ||
        role === "score-role"
      ) {
        return [
          { value: "1-2", label: "待教育局/厅审核" },
          { value: "2", label: "审核通过" },
          { value: "3-2", label: "教育局驳回" },
        ];
      }
      const opts = [];
      if (this.showDraftOption) {
        opts.push({ value: "0", label: "未提交" });
      }
      opts.push({ value: "1-1", label: "待学校审核" });
      opts.push({ value: "1-2", label: "待教育局/厅审核" });
      opts.push({ value: "2", label: "审核通过" });
      opts.push({ value: "3-1", label: "学校驳回" });
      opts.push({ value: "3-2", label: "教育局驳回" });
      return opts;
    },
  },
  methods: {
    emitChange(patch) {
      this.$emit("change", {
        keyword: this.keyword,
        auditState: this.auditState,
        year: this.year,
        ...patch,
      });
    },
    onKeyword(e) {
      this.$emit("update:keyword", e.target.value);
      this.emitChange({ keyword: e.target.value });
    },
    /** 点击搜索图标：直接触发查询（关键词为空也调用，不做判空） */
    onSearch() {
      this.$emit("search");
    },
    onStatus(e) {
      this.$emit("update:auditState", e.target.value);
      this.emitChange({ auditState: e.target.value });
    },
    onYearOpenChange(status) {
      this.yearOpen = status;
    },
    onYearPanelChange(value) {
      this.$emit("update:year", value);
      this.yearOpen = false;
      this.emitChange({ year: value });
    },
    onYearClear(value) {
      // 清空时 change 会触发
      if (!value) {
        this.$emit("update:year", null);
        this.emitChange({ year: null });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ps-filter-bar {
  display: flex;
  gap: 8px;
  padding: 13px 15px 0;
  flex-wrap: wrap;
  align-items: center;
  background: transparent;
  font-family: "Microsoft YaHei", "微软雅黑", sans-serif;

  &__search {
    position: relative;
    width: 293px;
    max-width: 100%;
  }

  &__search-icon {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 17px;
    height: 17px;
    cursor: pointer;
  }

  &__spacer {
    flex: 1;
    min-width: 12px;
  }

  &__year {
    display: flex;
    align-items: center;
  }
}

.ps-input,
.ps-select {
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  height: 36px;
  padding: 0 12px;
  font-size: 14px;
  font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
  background: #fff;
  color: #0b0b0b;
  box-sizing: border-box;
}

.ps-input {
  width: 100%;
  padding-right: 36px;
  &::placeholder {
    color: #898989;
  }
}

.ps-select {
  min-width: 103px;
  appearance: none;
  &--wide {
    min-width: 148px;
  }
  background-image: linear-gradient(45deg, transparent 50%, #666 50%),
    linear-gradient(135deg, #666 50%, transparent 50%);
  background-position: calc(100% - 16px) 15px, calc(100% - 11px) 15px;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  padding-right: 28px;
}

.ps-year-picker {
  ::v-deep .ant-input {
    height: 36px;
    border-radius: 4px;
    border-color: #e5e5e5;
    font-size: 14px;
  }
}

.ps-btn {
  border-radius: 4px;
  height: 34px;
  padding: 0 15px;
  font-size: 16px;
  font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
  line-height: 1;
  cursor: pointer;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  img {
    display: block;
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    object-fit: contain;
  }
  span {
    line-height: 1;
    /* 中文光学居中：几何居中后字形略偏上 */
    transform: translateY(1px);
  }
  &--blue {
    background: #2563eb;
    color: #fff;
    &:hover {
      background: #1d4ed8;
    }
  }
  &--outline-blue {
    background: #fff;
    border-color: #2563eb;
    color: #2563eb;
    &:hover {
      background: #eff6ff;
    }
  }
}
</style>
